import { NgRedux, select } from '@ptl/angular-redux-store';
import { LabelType } from '@angular-slider/ngx-slider';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { StateStructure } from '@ptl/state';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-anayltics.service';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PlansStateStructure } from '../configs/interfaces/plansStateStructure.interface';
import { plansNeedRefreshedFromBackend, savePlans, setActiveProductGroup } from '../configs/reducers/plans.reducer';
import { PRODUCT_GROUPS, PRODUCT_GROUP_NAMES } from '../enums/product-groups.enum';
import { PlanAttributes, SummaryView } from '../interfaces/plan-attributes.interface';
import { ProductTypeTabConfig } from '../interfaces/product-type-tab-config.interface';
import { Product } from '../interfaces/product.interface';
import { RecommendedPlans } from '../interfaces/recommended-plans.interface';
import { PlanUtilityService } from './plan-utility.service';
import { RetrievePlansService } from './retrieve-plans.service';

import { ProductTypeTabService } from '../services/product-type-tab.service';
import { SubProductTypeOrder } from '../interfaces/sub-product-type-order.interface';
import { ZipCountyStateStructure } from '../../shared/interfaces/zipCountyStateStructure.interface';
import { UntypedFormControl } from '@angular/forms';
import { setHardStops } from 'src/app/angular-elements/plan-selection/configs/reducers/plans.reducer';
import { comparePlansByIds, resetComparePlansChecked } from '../configs/reducers/plan-details.reducer';
import { HttpErrorResponse } from '@angular/common/http';
import { GetProductTypesResponse } from '../interfaces/get-product-types.interfaces';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BooleanInput, ContentStorageService } from '@ptl/global-components';
import { AuthoringModeService } from 'src/app/shared/services/authoring-mode.service';
import { EffectiveDateStateStructure } from '../../shared/components/configs/interfaces/effective-date.state-structure.interface';
import { PlanSummaryListPaginatorStateStructure } from '../plan-summary-list-paginator/configs/interfaces/plan-summary-list-paginator.state.interface';

import { PLAN_FILTER_REDUCER } from '../plan-filter/configs/reducers/plan-filter.reducer';
import { PlanFilterStateStructure } from '../plan-filter/configs/reducers/interfaces/plan-filter.state.interface';
import { PlanAttributeFilterPipe } from '../plan-filter/pipes/plan-filter.pipe';
import { PlanPriceSliderComponent } from '../plan-price-slider/plan-price-slider.component';

import { Observable, Subject } from "rxjs";

import { GlobalShadowStyleDirective } from '@ptl/global-components';

export interface PriceSliderConfig {
  planMin: number;
  planMax: number;
  options: {
    floor: number,
    ceil: number,
    translate: (value: number, label: LabelType) => string
  };
  showSlider: boolean;
}

@Component({
    selector: 'app-plan-summary-list',
    templateUrl: './plan-summary-list.component.html',
    styleUrls: ['./plan-summary-list.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class PlanSummaryListComponent extends GlobalShadowStyleDirective implements OnInit, AfterViewInit {

  @Input() useContentStorage?: boolean = false;

  /** URLS */
  @Input() viewPlansUrl: string;
  @Input() recommendedPlansUrl: string;
  @Input() comparePlansUrl: string;
  @Input() enrollStartUrl: string;
  @Input() specialEnrollmentUrl: string;
  @Input() planDetailsUrl: string;
  @Input() whbEnrollStartUrl: string;
  @Input() addMedicationsUrl: string;
  @Input() agencyLandingPageUrl: string;

  @Input() noZipCountyDataMessage: string;
  @Input() recommendedPlanLabelOne: string;
  @Input() recommendedPlanLabelTwo: string;
  @Input() recommendedPlanLabelThree: string;
  @Input() isPlanChange: boolean;
  @Input() hardStops: string;

  @Input() subProductTypeOrder: string;
  @Input() isSimplifiedEnrollment: boolean;
  @Input() priceTooltip: string;
  @Input() starsTooltip: string;

  // Determines Individual / Senior
  @Input() market: string;

  // Get Error Message
  @Input() errorMessage: string;
  errorMessageSanitized: SafeHtml;

  @Input() sepHyperlinkText: string;
  @Input() sepHyperlinkUrl: string;
  @Input() sepTooltip: string;
  @Input() isSep: boolean;
  @Input() tobaccoTooltip: string;

  @Input() showPaginator: string;
  @Input() showFilters: string;

  @Input() planCardImageUrl: string;
  @Input() planCardImageLabel: string;
  @Input() planCardImageMatchers: string;

  @Input() enrollmentFlow: string;
  @Input() quoteButtonPage: string;
  @BooleanInput() @Input() showSaveQuoteButton: boolean;
  @Input() saveQuoteText: string;
  @Input() returnUrl: string;

  @BooleanInput() @Input() disableCoverageDetails: boolean;

  isAuthoring = false;
  displaySpinner = false;

  @Input() productTypeTabConfig: string;
  parsedProductTypeTabsConfig: ProductTypeTabConfig[] = [];
  productTabs: PRODUCT_GROUP_NAMES[];
  productConfigs: Map<PRODUCT_GROUP_NAMES, ProductTypeTabConfig> = new Map();
  products: Map<PRODUCT_GROUP_NAMES, Product<PlanAttributes, SummaryView>[]> = new Map();
  selectedTab = new UntypedFormControl(0);
  activeProductTab: PRODUCT_GROUP_NAMES = PRODUCT_GROUPS.MEDICARE_ADVANTAGE;
  productTypesAvailable: boolean;
  noProductsFound = false;
  productOrder: SubProductTypeOrder[];
  formError = false;

  @ViewChild('topOfProductList')
  scrollToThis: ElementRef;

  @ViewChild('slider', { static: false }) private slider: PlanPriceSliderComponent;
  priceSliderConfig: Map<PRODUCT_GROUPS, PriceSliderConfig> = new Map()

  comparablePlanIds: string[] = [];
  comparePlansCheckboxLimitSubject: Subject<number> = new Subject<number>();

  isRecommendPlansFlow = false;
  recommendedPlanIds: RecommendedPlans = null;

  region: string;
  outboundNYUrl: string;
  regionSpelledOut: string;

  @select(['componentStates', 'Plans', 'plansNeedRefreshedFromBackend']) planRefreshWatcher$: Observable<boolean>;
  getProductLabel = (productTab): PRODUCT_GROUP_NAMES => PRODUCT_GROUPS[productTab];

  constructor(
    private productTypeTabService: ProductTypeTabService,
    private planUtilityService: PlanUtilityService,
    private retrievePlansService: RetrievePlansService,
    private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private domSanitizer: DomSanitizer,
    private contentStorageService: ContentStorageService,
    private authoringMode: AuthoringModeService,
    private planAttributeFilterPipe: PlanAttributeFilterPipe
  ) { 
    super();
  }

  get showPaginatorBool(): boolean {
    return this.showPaginator === 'true' ? true : false;
  }

  get showFiltersBool(): boolean {
    return this.showFilters === 'true' ? true : false;
  }

  
  ngOnInit(): void {
    if(typeof this.errorMessage === 'undefined'){
      this.errorMessageSanitized = 'Plans not available for your current selection of Zip Code and County.';
    }else{
    this.errorMessageSanitized = this.domSanitizer.bypassSecurityTrustHtml(this.errorMessage);
    }
    this.isAuthoring = this.authoringMode.getAuthoringMode();
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    this.isPlanChange = planState.isPlanChange;
  }

  trackByProductId(index: number, product: Product<PlanAttributes, SummaryView>): string {
    return product.productId;
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.parseProductTypeOrderAsJson();
    this.ngRedux.dispatch(setHardStops(this.hardStops));

    // Configure product tabs from content storage if either plan change or content storage flag is set
    if (this.isPlanChange || this.useContentStorage) {
      this.configureContentStorage();
    }
    else if (this.productTypeTabConfig && this.productTypeTabConfig !== 'productTypeTabConfig') {
      this.initializePage();
    }
    else {
      console.error('Product Tabs Configuration is missing.');
      return;
    }
  }

  initializePage() {
    // Parse the AEM configuration for the tab labeled "Product Type Tabs Config"
    this.parseProductTabsConfig();
    
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');

    if ((planState?.productGroupsForCounty && planState?.plansByPlanType) || this.isAuthoring) {
      let productGroupsForCounty;

      // Show all products configured regardless of region for Author mode
      if (this.isAuthoring) {
        productGroupsForCounty = this.productConfigs.keys();
      } else {
        productGroupsForCounty = planState.productGroupsForCounty;
      }

      // Filter the products to only those present in productGroupsForCounty
      this.setProductTabs(productGroupsForCounty);

      // Set the initial active product based on the saved state value, or the first tab if this is the first time on the page
      this.initActiveProductTab();

      // Builds a map to access all plans for a given product key
      this.setProductsPlanMap();

      // Set recommended plans
      this.setRecommendedPlans();

      // Parse out and save manually entered plan cards in AEM. Found in tab "Product Type Tabs Config" in the "Inject a Plan Card" fields
      this.configureProductTabs();

      // Clear any compare checkboxes from the saved state after processing plan cards
      this.resetComparePlans();

      // Let AEM authors see the page as it should be
      if (this.isAuthoring) {
        this.displaySpinner = false;
        this.noProductsFound = false;
        return;
      }
    }
    // If we don't have any product groups saved for the current region, we trigger a plan refresh to pull them
    else {
      this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
    }

    // Sets subscriptions to refresh plans and change product types
    this.setSubscriptions();
  }

  shouldShowForm(productTab) {
    if (!this.formError && this.productConfigs.get(productTab)?.showForm && this.productConfigs.get(productTab)?.formConfigStrategy) {
      return true;
    }
    
    return false;
  }

  filteredAmount(productTab): string {
    const products: Product<PlanAttributes, SummaryView>[] = this.products.get(productTab);

    if (!products) { return null; }

    /** Only show filtered # if that's the tab we're currently filtering AND not showing a pre auth form or similar*/
    if (this.activeProductTab === productTab && !this.shouldShowForm(productTab)) {

      const totalProductsForType: number = products.length;
      const paginatorState: PlanSummaryListPaginatorStateStructure = this.reducerUtilityService.getState('PlanSummaryListPaginator');
      const filteredLength = paginatorState.totalLength;

      if (filteredLength === totalProductsForType) {
        return `${totalProductsForType}`;
      } else {
        return `${filteredLength}/${totalProductsForType}`;
      }
    } else {
      return `${products.length}`;
    }
  }

  parseProductTabsConfig() {
    this.parsedProductTypeTabsConfig = JSON.parse(this.productTypeTabConfig) as ProductTypeTabConfig[];

    this.parsedProductTypeTabsConfig.map(productTab => {
      const typeCorrectedProductTabConfig = this.parseTypes(productTab) as ProductTypeTabConfig;
      if (this.isAuthoring) {
        typeCorrectedProductTabConfig.staticTab = true;
      }
      this.productConfigs.set(productTab.productType, typeCorrectedProductTabConfig);
    });
    console.log(this.productConfigs);
  }

  parseProductTypeOrderAsJson(): void {
    if (this.subProductTypeOrder) {
      try {
        this.productOrder = JSON.parse(this.subProductTypeOrder) as SubProductTypeOrder[];
      } catch (err) {
        console.error(err);
      }
    }
    return null;
  }

  // Plan Services and Behaviors
  setSubscriptions() {
    const here = this;

    this.productTypeTabService.productTypesReturned$.subscribe((productsResponse: GetProductTypesResponse) => {
      if (productsResponse?.productGroups) {

        this.setProductTabs(productsResponse.productGroups);
        this.initActiveProductTab();

        this.retrievePlansService.getPlans(productsResponse.productGroups, null, this.productTabs).then((combinedPlansResponse) => {
          let errorCheck = false;

          for (const productGroup in combinedPlansResponse as any) {
            if (combinedPlansResponse[productGroup].length <= 0) {
              errorCheck = true;

              if (this.productConfigs.get(productGroup as PRODUCT_GROUP_NAMES).showForm) {
                here.toggleForm(productGroup as PRODUCT_GROUP_NAMES);
              }
            }
          }

          if (errorCheck === false) {
            this.formError = false;
          } else {
            this.formError = true;
          }

          here.setProductsPlanMap();
          here.configureProductTabs();
          this.resetComparePlans();
          here.setRecommendedPlans();
        })
        .finally(() => {
          this.displaySpinner = false;
        });
      }

      // TODO: ADD the below error message with custom error message to have different error messages for plans not found and age hardstop.
      // (productsResponse as unknown as HttpErrorResponse).message === 'Age Hardstop'
      if (
        (productsResponse as unknown as HttpErrorResponse).error ||
        (productsResponse as unknown as HttpErrorResponse).message === 'Plans not found!'
      ) {
        this.displaySpinner = false;
        this.noProductsFound = true;
      }
    });

    this.planRefreshWatcher$.subscribe(async (refresh: boolean) => {
      if (refresh === true) {
        const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
        this.noProductsFound = false;

        if (zipCountyState.county && zipCountyState.county.region) {
          this.displaySpinner = true;
          this.scrollToThis?.nativeElement.scrollIntoView({ behavior: 'smooth' });

          // This service call will end up triggering the this.productTypeTabService.productTypesReturned$ observable 
          // That is where the rest of the plan loading logic can be found
          await this.productTypeTabService.retrieveProductTypes(this.market, this.productTabs);
        }
        else {
          this.displaySpinner = true;
        }
      }
    });
  }

  configureContentStorage() {
    this.contentStorageService.contentChanged$.subscribe(response => {
      if (response) {
        const config = response.get('PlanSummaryListContent');

        if (config) {
          // Override current configuration values with any values defined in the content storage map
          this.market = this.getContentConfigValue(this.market, config.get('market'));
          this.priceTooltip = this.getContentConfigValue(this.priceTooltip, config.get('priceTooltip'));
          this.starsTooltip = this.getContentConfigValue(this.starsTooltip, config.get('starsTooltip'));
          this.subProductTypeOrder = this.getContentConfigValue(this.subProductTypeOrder, config.get('subProductTypeOrder'));
          this.productTypeTabConfig = this.getContentConfigValue(this.productTypeTabConfig, config.get('productTypeTabConfig'));
          this.isSimplifiedEnrollment = this.getContentConfigValue(this.isSimplifiedEnrollment, config.get('isSimplifiedEnrollment'));

          if (this.isSimplifiedEnrollment) {
            this.recommendedPlanIds = null;
          }

          this.initializePage();
        }
      }
    });
  }

  // Only apply content storage values that are defined
  getContentConfigValue(existingValue: any, configVal: string) {
    if (configVal != null) {
      return configVal;
    }

    return existingValue;
  }

  setProductsPlanMap(): Map<PRODUCT_GROUP_NAMES, Product<PlanAttributes, SummaryView>[]> {
    this.products = this.planUtilityService.getAllPlansAsMap();
    return this.products;
  }

  setRecommendedPlans() {
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');

    this.isRecommendPlansFlow = planState.recommendPlansFlowUsageCode !== null;
    this.recommendedPlanIds = planState.recommendedPlanIds || null;
  }

  // Product Tabs Behavior
  setProductTabs(productGroups) {
    const here = this;

    const productGroupConfigList = [...this.productConfigs.keys()];
    const productGroupResponseList = Object.keys(productGroups);

    this.productTabs = productGroupConfigList.filter(productGroup => {
      return productGroupResponseList.includes(productGroup) || here.productConfigs.get(productGroup as PRODUCT_GROUP_NAMES).staticTab;
    }).sort((a, b) => {
      return here.productConfigs.get(a).order - here.productConfigs.get(b).order;
    });
    
    // If not products are available at this point we do not have any for the current region
    if (!this.productTabs?.length) {
      this.noProductsFound = true;
    }
  }

  initActiveProductTab() {
    this.activeProductTab = (() => {
      const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');

      const planTypeFromURLValue: PRODUCT_GROUPS = this.productTypeTabService.getProductTypeFromURL();
      const stateValue = planState?.activeProductGroup;
      const firstValue = this.productTabs[0];

      /**
       * Medigap -> WHB via form config 'a href' or refresh (refresh would be similar to the stateValue I suppose)
       */
      if (planTypeFromURLValue && this.productTabs.includes(planTypeFromURLValue)) {
        return planTypeFromURLValue;
      } else if (stateValue && this.productTabs.includes(stateValue)) {
        return stateValue;
      } else {
        return firstValue;
      }
    })();

    this.setActiveProductTab(this.activeProductTab);
  }

  setActiveProductTab(productName: PRODUCT_GROUP_NAMES) {
    this.activeProductTab = productName;
    const activeProductTabIndex = this.productTabs.indexOf(productName);
    this.selectedTab.setValue(activeProductTabIndex);
    this.ngRedux.dispatch(
      setActiveProductGroup(productName as PRODUCT_GROUP_NAMES)
    );
  }

  configureProductTabs() {
    // Store Configuration in Content Storage
    this.productTabs?.forEach((productTab: PRODUCT_GROUP_NAMES) => {

      if (this.productConfigs.get(productTab)?.injectCards) {
        try {
          /**
           * Need to save the injected product cards/types into state
           * so that sub-functionalities (like the price slider)
           * can continue to work as intended.
           *
           * Previously, an injected card was ignored by the price slider due to lack of
           * plans returned from state, and would set the price to 0 on min&max,
           * resulting in the filtering out of the injected plan card
           */
          const injectedCards = JSON.parse(this.productConfigs.get(productTab).injectCards) as Product<PlanAttributes, SummaryView>[];
          const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans') || {} as PlansStateStructure;

          // Check if we should force this plan to exist when we have no plans from the remote service
          if (!planState.plansByPlanType && injectedCards[0].alwaysShowWhenNoPlans) {
            planState.plansByPlanType = {};
          }

          // If the injected card doesn't have this object then we won't show any plans
          if (!planState.plansByPlanType) {
            this.noProductsFound = true;
            return;
          }

          // Set the correct plans using the injected card type
          planState.plansByPlanType[productTab] = { 0: injectedCards[0] };

          // This'll be used in other files...
          this.ngRedux.dispatch(savePlans(planState.plansByPlanType));
          // ...this'll be used in this file.
          this.products.set(productTab, injectedCards);

        } catch (err) {
          console.error(err);
        }
      }

      if (this.productConfigs.get(productTab)?.showSlider) {
        this.configurePriceSlider(productTab);
      }

      if (this.productConfigs.get(productTab)?.showForm && this.productConfigs.get(productTab)?.formBehavior) {
        this.toggleForm(productTab);
      }
    });
  }

  public productChanged(event: MatTabChangeEvent) {
    const productTabSelected = event.tab.textLabel;

    const productGroups = [...this.productConfigs.keys()];
    productGroups.forEach(productName => {
      if (productName === productTabSelected) {

        this.setActiveProductTab(productTabSelected as PRODUCT_GROUP_NAMES);

        this.googleAnalyticsService.eventEmitter(
          {
            event: 'virtualPageview',
            pagePath: document.URL,
            pageTitle: document.title
          }
        );
      }
    });
  }

  // Form Behavior Functions
  showForm(productTab: PRODUCT_GROUP_NAMES, el: HTMLElement, event?: { clicked: boolean }) {
    this.scrollToThis?.nativeElement.scrollIntoView({ behavior: 'smooth' });
    this.formError = false;
    this.productConfigs.get(productTab).showForm = true;
  }

  toggleForm(productTab: PRODUCT_GROUP_NAMES) {
    /**
     * If the form returned errored, there's no further logic needed, flag to keep form hidden
     * to show errorMessage + form refresh
     * */
    if (this.formError) {
      this.productConfigs.get(productTab).showForm = false;
    } else {
      switch (this.productConfigs.get(productTab).formBehavior) {
        case 'optional':
          this.productConfigs.get(productTab).showForm = false;
          break;
        case 'required':
          this.productConfigs.get(productTab).showForm = this.products.get(productTab)?.length > 0 &&
            this.products.get(productTab)?.every((product) => product.allowEnroll) ? false : true;
          break;
      }
    }
  }

  /** PRICE SLIDER FUNCTIONS */
  // Triggered when manually interacting with filters from view
  rebuildPriceSlider() {
    this.productTabs?.forEach((productTab: PRODUCT_GROUP_NAMES) => {
      if (this.productConfigs.get(productTab)?.showSlider) {
        this.configurePriceSlider(productTab);
      }
    })
  }
  // Builds the price slider and manually sets values each time
  // * min/max
  // * manually set to min/max when filters change
  configurePriceSlider(productTab) {

    const plans = this.products.get(productTab);
    let minAndMaxPrices = null;

    /** Check for filters, before configuring the price slider */
    const filters: PlanFilterStateStructure = this.reducerUtilityService.getComponentStates()[PLAN_FILTER_REDUCER];

    // At least one filter active
    if (Object.keys(filters.activeFilters).length > 0) {
      const filteredPlans = this.planAttributeFilterPipe.transform(plans, productTab);
      minAndMaxPrices = this.planUtilityService.getPlanMinimumAndMaximumPrices(filteredPlans);
    } else {
      minAndMaxPrices = this.planUtilityService.getPlanMinimumAndMaximumPrices(plans);
    }

    const planMin = minAndMaxPrices.min;
    const planMax = minAndMaxPrices.max;
    let showSlider = false;
    let options = {
      floor: 0,
      ceil: 0,
      translate: (value: number, label: LabelType) => {
        return '';
      }
    };

    if (planMin > -1 && planMax < Infinity) {
      showSlider = planMin !== planMax;
      options = {
        floor: planMin,
        ceil: planMax,
        translate: (value: number, label: LabelType): string => {
          switch (label) {
            case LabelType.Low:
              return '<b>Min price:</b> $' + value;
            case LabelType.High:
              return '<b>Max price:</b> $' + value;
            case LabelType.Floor:
              return 'MIN $' + value;
            case LabelType.Ceil:
              return 'MAX $' + value;
            default:
              return '$' + value;
          }
        }
      };
    }

    // Initial new-config setup
    this.priceSliderConfig.set(productTab, {
      planMax,
      planMin,
      showSlider,
      options
    });

    // Manually setup slider values ( specifically used when interacting with the filter and triggering a reconfiguration)
    // TODO: consider moving this to the slider itself and leveraging the .manualRefresh functionality of the ngxslider component
    if (this.slider) {
      this.slider.value = this.priceSliderConfig.get(this.slider.id as PRODUCT_GROUPS).planMin;
      this.slider.highValue = this.priceSliderConfig.get(this.slider.id as PRODUCT_GROUPS).planMax;
    }
  }

  priceSliderValuesChanged(event: { value: number, highValue: number }, productTab) {
    const priceSliderConfig = this.priceSliderConfig.get(productTab);
    if (priceSliderConfig) {
      if (event.value !== undefined) {
        priceSliderConfig.planMin = event.value;
      }
      if (event.highValue !== undefined) {
        priceSliderConfig.planMax = event.highValue;
      }

      this.priceSliderConfig.set(productTab, priceSliderConfig);
    }
  }

  getImageForProductType(productType: PRODUCT_GROUP_NAMES) {
    const fileReference = (this.productConfigs).get(productType).type_fileReference;
    return fileReference;
  }

  // Compare Plans Functions
  resetComparePlans() {
    /** Retrieve state values, to allow component to identify # checked box */
    this.ngRedux.dispatch(resetComparePlansChecked());
    this.comparablePlanIds = [];

    // clear out compare plan ids
    this.ngRedux.dispatch(comparePlansByIds(this.comparablePlanIds));

    /** Emit total number of checkboxes check. Used inside app plan cards to determine if they can enable checkbox*/
    this.comparePlansCheckboxLimitSubject.next(this.comparablePlanIds.length);
  }

  comparePlanToggle(event: { planId: string, checked: boolean }) {
    if (event.checked) {
      this.comparablePlanIds.push(event.planId);
    } else {
      this.comparablePlanIds = this.comparablePlanIds.filter((planId: string) => {
        return planId !== event.planId;
      });
    }

    /** Emit number of checked boxes to children */
    this.comparePlansCheckboxLimitSubject.next(this.comparablePlanIds.length);

    /** Dispatch current checked to state */
    this.ngRedux.dispatch(comparePlansByIds(this.comparablePlanIds));
  }

  goToComparePlans() {
    window.location.href = `./${this.comparePlansUrl}`;
  }

  // Utility Function
  parseTypes(obj) {
    Object.keys(obj).forEach(key => {
      if (obj[key].toLowerCase() === 'true') {
        obj[key] = true;
      } else if (obj[key].toLowerCase() === 'false') {
        obj[key] = false;
      } else if (!isNaN(Number(obj[key]))) {
        obj[key] = Number(obj[key]);
      }
    });
    return obj;
  }

  getSanitizedHTMLContent(content: string): SafeHtml {
    if (content) {
      return this.domSanitizer.bypassSecurityTrustHtml(content);
    }
  }
}
